import { useEffect, useRef, useState } from "react";

const TYPE_MS = 45;
const DELETE_MS = 25;
const HOLD_FULL_MS = 1800;
const HOLD_EMPTY_MS = 400;
const INITIAL_DELAY_MS = 600;
const CARET_BLINK_MS = 530;
const CARET_CHAR = "▏";

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useTypewriterPlaceholder(suggestions, { inputRef }) {
  const reducedMotion = prefersReducedMotion();
  const [text, setText] = useState("");
  const [caretOn, setCaretOn] = useState(true);
  const [moving, setMoving] = useState(false);
  const [inputEl, setInputEl] = useState(
    inputRef && inputRef.current ? inputRef.current : null
  );

  const indexRef = useRef(0);
  const charRef = useRef(0);
  const phaseRef = useRef("typing");
  const timeoutRef = useRef(null);
  const focusedRef = useRef(false);
  const visibleRef = useRef(
    typeof document === "undefined" ? true : document.visibilityState !== "hidden"
  );
  const runningRef = useRef(false);

  useEffect(() => {
    const current = inputRef && inputRef.current ? inputRef.current : null;
    if (current !== inputEl) setInputEl(current);
  });

  useEffect(() => {
    if (reducedMotion) return undefined;
    const input = inputEl;
    if (!input) return undefined;

    const clearTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const canRun = () =>
      !focusedRef.current && visibleRef.current && (input.value || "") === "";

    const tick = () => {
      if (!canRun()) {
        runningRef.current = false;
        setMoving(false);
        return;
      }
      const word = suggestions[indexRef.current] || "";
      const phase = phaseRef.current;

      if (phase === "typing") {
        if (charRef.current < word.length) {
          charRef.current += 1;
          setText(word.slice(0, charRef.current));
          setMoving(true);
          timeoutRef.current = setTimeout(tick, TYPE_MS);
        } else {
          phaseRef.current = "holdFull";
          setMoving(false);
          timeoutRef.current = setTimeout(tick, HOLD_FULL_MS);
        }
      } else if (phase === "holdFull") {
        phaseRef.current = "deleting";
        timeoutRef.current = setTimeout(tick, 0);
      } else if (phase === "deleting") {
        if (charRef.current > 0) {
          charRef.current -= 1;
          setText(word.slice(0, charRef.current));
          setMoving(true);
          timeoutRef.current = setTimeout(tick, DELETE_MS);
        } else {
          phaseRef.current = "holdEmpty";
          setMoving(false);
          indexRef.current = (indexRef.current + 1) % suggestions.length;
          timeoutRef.current = setTimeout(tick, HOLD_EMPTY_MS);
        }
      } else {
        phaseRef.current = "typing";
        timeoutRef.current = setTimeout(tick, 0);
      }
    };

    const start = (delay) => {
      if (runningRef.current) return;
      if (!canRun()) return;
      runningRef.current = true;
      timeoutRef.current = setTimeout(tick, delay || 0);
    };

    const stop = () => {
      runningRef.current = false;
      clearTimer();
      setMoving(false);
    };

    const onFocus = () => {
      focusedRef.current = true;
      stop();
      setText("");
      if (phaseRef.current === "typing" || phaseRef.current === "holdFull") {
        indexRef.current = (indexRef.current + 1) % suggestions.length;
      }
      phaseRef.current = "typing";
      charRef.current = 0;
    };

    const onBlur = () => {
      focusedRef.current = false;
      if ((input.value || "") === "") start(HOLD_EMPTY_MS);
    };

    const onVisibility = () => {
      visibleRef.current = document.visibilityState !== "hidden";
      if (!visibleRef.current) {
        clearTimer();
        runningRef.current = false;
      } else {
        start(0);
      }
    };

    input.addEventListener("focus", onFocus);
    input.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVisibility);
    start(INITIAL_DELAY_MS);

    return () => {
      stop();
      input.removeEventListener("focus", onFocus);
      input.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [inputEl, reducedMotion, suggestions]);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const id = setInterval(() => setCaretOn((v) => !v), CARET_BLINK_MS);
    return () => clearInterval(id);
  }, [reducedMotion]);

  if (reducedMotion) return suggestions[0] || "";
  const showCaret = !moving && caretOn;
  return text + (showCaret ? CARET_CHAR : "");
}
